import { formatBytes } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * What the artifacts domain's refusals say, in Brazilian Portuguese (checked against the
 * English shape at compile time).
 *
 * Register is `você`, informal-professional. *Baixar* é o verbo brasileiro — nunca
 * *descarregar*; *arquivo* (m.) é o nome do que a pessoa clica — nunca *ficheiro*.
 *
 * ── Sobre *artefato*, que o ADR-0018 não ratificou ──
 *
 * A lista de entidades do ADR-0018 não traz *artifact*, e este catálogo precisou de uma
 * palavra. Ela já existe no produto: `activity.ts` diz **Artefatos** (m.) no cartão de
 * armazenamento, e uma segunda palavra aqui seria justamente a deriva que a lista ratificada
 * existe para impedir. *Arquivo* foi descartado por motivo oposto e mais forte: já é o nome
 * do que alguém **envia** para uma base de conhecimento, e reaproveitá-lo daria uma palavra
 * para duas ideias. Fica *artefato* (m.), pendente de ratificação.
 *
 * ── Concordância, e por que ela é segura aqui ──
 *
 * Em duas frases o sujeito é o nome do arquivo entre aspas, e não um substantivo desta
 * língua. Quem manda na concordância é a entidade que o nome designa — *o artefato* (m.),
 * *o arquivo* (m.) —, os dois masculinos, então *recusado*, *verificado* e *baixado* ficam no
 * masculino em todos os casos. Se a palavra ratificada vier feminina, estes três particípios
 * mudam junto.
 */
type ArtifactsMessages = Pick<Messages['errors'], Extract<keyof Messages['errors'], `artifacts.${string}`>>

/** This file's own locale — see the English sibling for why an entry names it. */
const LOCALE = 'pt-BR'

export const artifacts: ArtifactsMessages = {
  // --- produzir um ----------------------------------------------------------
  'artifacts.needs_a_name': () => 'Um artefato precisa de um nome.',
  'artifacts.too_large': ({ name, sizeBytes, limitBytes }) =>
    `“${name}” tem ${formatBytes(LOCALE, sizeBytes)}, e um artefato pode ter até ` +
    `${formatBytes(LOCALE, limitBytes)}.`,

  // --- ler um ---------------------------------------------------------------
  //
  // A primeira frase não nomeia nada, de propósito: ela responde "não existe" e "não é seu"
  // com as mesmas palavras, para que o endpoint não revele o trabalho de outra pessoa.
  'artifacts.not_available': () => 'Nenhum artefato com esse id está disponível para você.',
  'artifacts.refused_by_content_check': ({ name }) =>
    `“${name}” foi recusado pela verificação de conteúdo e não pode ser baixado.`,
  'artifacts.not_checked_yet': ({ name }) => `“${name}” ainda não foi verificado e não pode ser baixado.`
}
